import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Cable, Filter, RefreshCw, Search, ShieldCheck, Sparkles } from 'lucide-react';
import { toast } from '../ui/sonner';
import EmbeddedConnectionsHub, { PublerConnectionCard, MakeComCard, WatiConnectionCard } from '../connections/EmbeddedConnectionsHub';
import AuthorizationLinksHub from './AuthorizationLinksHub';
import { MlmConnections } from '../mlm/MlmConnections';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_OPTIONS = ['planned', 'pending_credentials', 'ready_to_connect', 'connected'];
const STATUS_LABELS = {
  planned: 'planned',
  pending_credentials: 'pendiente',
  ready_to_connect: 'listo para conectar',
  connected: 'conectado',
};

const STATUS_STYLES = {
  planned: 'border border-white/10 text-text-muted bg-white/5',
  pending_credentials: 'border border-amber-400/20 text-amber-200 bg-amber-500/10',
  ready_to_connect: 'border border-sky-400/20 text-sky-200 bg-sky-500/10',
  connected: 'border border-primary/20 text-primary bg-primary/10',
};

const IntegrationChecklistCard = ({ item }) => (
  <div className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`integration-readiness-${item.provider}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-semibold text-text-heading">{item.name}</p>
        <p className="mt-1 text-xs text-text-body">{item.next_step}</p>
      </div>
      <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[item.status] || STATUS_STYLES.planned}`}>{STATUS_LABELS[item.status] || item.status}</span>
    </div>
    {!!item.missing_credentials?.length && (
      <div className="mt-3 flex flex-wrap gap-2" data-testid={`integration-readiness-missing-${item.provider}`}>
        {item.missing_credentials.map((credential) => <span key={credential} className="rounded-full border border-amber-300/20 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-amber-100">{credential}</span>)}
      </div>
    )}
  </div>
);

const MetricCard = ({ label, value, helper, testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
    <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
    <p className="mt-2 text-xs text-text-muted">{helper}</p>
  </div>
);

const SummaryPanel = ({ title, items, testId }) => (
  <div className="rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Radar</p>
    <h4 className="mt-2 text-lg font-semibold text-text-heading">{title}</h4>
    <div className="mt-4 space-y-2 text-sm text-text-body">
      {items.map((item) => <div key={item}>• {item}</div>)}
    </div>
  </div>
);

const FeaturedProviderCard = ({ provider, updateProvider, savingKey }) => (
  <div className="rounded-[28px] border border-border-subtle bg-[#161616]/90 p-5" data-testid={`integrations-featured-provider-${provider.provider_key}`}>
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-white/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{provider.category}</span>
          <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[provider.status] || STATUS_STYLES.planned}`}>{STATUS_LABELS[provider.status] || provider.status}</span>
        </div>
        <h4 className="mt-3 text-xl font-semibold text-text-heading">{provider.name}</h4>
        <p className="mt-2 text-sm text-text-body">{provider.why}</p>
      </div>
      <div className="flex h-12 w-12 items-center justify-center rounded-[18px] border border-primary/20 bg-primary/10 text-primary">
        {provider.status === 'connected' ? <ShieldCheck className="h-5 w-5" /> : <Cable className="h-5 w-5" />}
      </div>
    </div>

    <div className="mt-5 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
      <div className="space-y-3">
        <div>
          <label className="mb-2 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Estado operativo</label>
          <select className="input-kinesthetic" value={provider.status} onChange={(e) => updateProvider(provider.provider_key, { status: e.target.value })} data-testid={`integration-status-${provider.provider_key}`}>
            {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{STATUS_LABELS[status]}</option>)}
          </select>
        </div>
        <div>
          <label className="mb-2 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Cuenta / label</label>
          <input className="input-kinesthetic" defaultValue={provider.account_label || ''} placeholder="Ej: neuralia principal" onBlur={(e) => updateProvider(provider.provider_key, { account_label: e.target.value })} data-testid={`integration-label-${provider.provider_key}`} />
        </div>
      </div>

      <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4">
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Siguiente paso</p>
        <p className="mt-2 text-sm text-text-body" data-testid={`integration-next-step-${provider.provider_key}`}>{provider.next_step}</p>
        {!!provider.missing_credentials?.length && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid={`integration-missing-${provider.provider_key}`}>
            {provider.missing_credentials.map((item) => <span key={item} className="rounded-full border border-amber-300/20 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-amber-100">{item}</span>)}
          </div>
        )}
      </div>
    </div>

    <div className="mt-4 grid gap-4 md:grid-cols-2">
      <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4">
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Servicios</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {provider.services.map((item) => <span key={item} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{item}</span>)}
        </div>
      </div>
      <div className="rounded-[20px] border border-border-subtle bg-card/40 p-4">
        <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Necesitaremos</p>
        <div className="mt-3 space-y-2 text-sm text-text-body">
          {provider.required_credentials.map((item) => <div key={item}>• {item}</div>)}
        </div>
      </div>
    </div>

    <div className="mt-4">
      <label className="mb-2 block text-[10px] uppercase tracking-[0.18em] text-text-muted">Notas operativas</label>
      <textarea className="input-kinesthetic resize-none" rows={3} defaultValue={provider.notes || ''} placeholder="Qué falta, qué haremos mañana, scopes, cuenta principal..." onBlur={(e) => updateProvider(provider.provider_key, { notes: e.target.value })} data-testid={`integration-notes-${provider.provider_key}`} />
    </div>

    {savingKey === provider.provider_key && <p className="mt-3 text-xs text-primary">Guardando...</p>}
  </div>
);

export default function IntegrationsCenterHub({ authFetch }) {
  const [hub, setHub] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // API wrapper for admin Nango/Telegram connections (reuses MlmConnections component)
  const adminNangoApi = useCallback(async (path, options = {}, opts = {}) => {
    try {
      const res = await authFetch(`${API}/admin${path}`, { ...options, headers: { 'Content-Type': 'application/json', ...(options.headers || {}) } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error en la operación');
      return data;
    } catch (err) {
      throw err;
    }
  }, [authFetch]);

  const loadHub = async () => {
    try {
      const [hubRes, readinessRes] = await Promise.all([
        authFetch(`${API}/admin/integrations-hub`),
        authFetch(`${API}/admin/integrations-hub/readiness`),
      ]);
      const hubData = await hubRes.json();
      const readinessData = await readinessRes.json();
      if (!hubRes.ok) throw new Error(hubData.detail || 'No se pudo cargar el centro de conexiones');
      if (!readinessRes.ok) throw new Error(readinessData.detail || 'No se pudo cargar el readiness de integraciones');
      setHub(hubData);
      setReadiness(readinessData);
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar el centro de conexiones');
    }
  };

  useEffect(() => {
    loadHub();
  }, []);

  const updateProvider = async (providerKey, payload) => {
    try {
      setSavingKey(providerKey);
      const res = await authFetch(`${API}/admin/integrations-hub/${providerKey}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo actualizar la conexión');
      toast.success('Conexión actualizada');
      loadHub();
    } catch (error) {
      toast.error(error.message || 'No se pudo actualizar la conexión');
    } finally {
      setSavingKey(null);
    }
  };

  const providers = hub?.providers || [];
  const categories = useMemo(() => Array.from(new Set(providers.map((item) => item.category))).sort(), [providers]);

  const filteredProviders = useMemo(() => {
    const term = search.trim().toLowerCase();
    return providers.filter((provider) => {
      const searchMatch = !term || [
        provider.name,
        provider.category,
        provider.why,
        provider.next_step,
        ...(provider.services || []),
        ...(provider.required_credentials || []),
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
      const statusMatch = statusFilter === 'all' || provider.status === statusFilter;
      const categoryMatch = categoryFilter === 'all' || provider.category === categoryFilter;
      return searchMatch && statusMatch && categoryMatch;
    });
  }, [providers, search, statusFilter, categoryFilter]);

  const featuredProviders = useMemo(() => {
    const score = { connected: 0, ready_to_connect: 1, pending_credentials: 2, planned: 3 };
    return [...providers].sort((a, b) => (score[a.status] ?? 9) - (score[b.status] ?? 9)).slice(0, 4);
  }, [providers]);

  if (!hub) return <div className="glass-card p-8 text-center text-text-muted" data-testid="integrations-hub-loading">Cargando centro de conexiones...</div>;

  return (
    <div className="space-y-6" data-testid="integrations-hub">
      <div className="glass-card tracing-panel overflow-hidden" data-testid="integrations-hub-hero-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-3">Centro de conexiones</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Diagnóstico aplicado · integrations browser modular</p>
            <h3 className="mt-4 max-w-4xl text-3xl font-semibold text-text-heading">Ahora las integraciones se leen como un stack operativo real: qué está listo, qué desbloquea ventas y qué falta cerrar antes del viernes.</h3>
            <p className="mt-4 max-w-3xl text-sm text-text-body">Antes era un listado correcto, pero demasiado lineal. Ahora se comporta como un browser interno de proveedores con prioridades visibles, filtros y estados accionables.</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="integrations-diagnosis-chips">
              <span className="insight-chip">Prioridad visual</span>
              <span className="insight-chip">Stack operativo</span>
              <span className="insight-chip">Gaps claros</span>
              <span className="insight-chip">Activación rápida</span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard label="Total" value={hub.metrics.total} helper="proveedores planificados" testId="integrations-hub-total" />
            <MetricCard label="Conectados" value={hub.metrics.connected} helper="integraciones ya activas" testId="integrations-hub-connected" />
            <MetricCard label="Pendientes" value={hub.metrics.pending_credentials} helper="faltan credenciales" testId="integrations-hub-pending" />
            <MetricCard label="Listos" value={hub.metrics.ready_to_connect} helper="esperando activación" testId="integrations-hub-ready" />
          </div>
        </div>
      </div>

      <EmbeddedConnectionsHub authFetch={authFetch} mode="admin" />

      {/* ── Automatización Social: Publer + Make.com ──────────────────────── */}
      <div className="glass-card tracing-panel overflow-hidden" data-testid="admin-social-automation-section">
        <div className="border-b border-border-subtle px-6 py-4">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Automatización Social · Sin verificación de API</p>
          <h4 className="mt-1 text-lg font-semibold text-text-heading">Publer + Make.com — Publicación automática TikTok / Instagram / más</h4>
          <p className="mt-1 text-sm text-text-body">Saltea las restricciones de verificación de TikTok y Meta publicando vía Publer. Usa Make.com para orquestar todos los flujos de Neuralia369.</p>
        </div>
        <div className="grid gap-4 p-4 md:grid-cols-2">
          <PublerConnectionCard authFetch={authFetch} isAdmin={true} />
          <MakeComCard authFetch={authFetch} />
        </div>
      </div>

      {/* ── WhatsApp Business: WATI ────────────────────────────────────────── */}
      <div className="glass-card tracing-panel overflow-hidden" data-testid="admin-wati-section">
        <div className="border-b border-border-subtle px-6 py-4">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">WhatsApp Business API · BSP Oficial Meta</p>
          <h4 className="mt-1 text-lg font-semibold text-text-heading">WATI — Campañas WhatsApp para Perú</h4>
          <p className="mt-1 text-sm text-text-body">Activa mensajes automáticos para leads de /activa-peru sin depender de Meta directamente. Activación por llamada de voz (no SMS).</p>
        </div>
        <div className="p-4">
          <WatiConnectionCard authFetch={authFetch} />
        </div>
      </div>

      {/* ── Conexiones directas Neuralia369 (Nango + Telegram) ───────────── */}
      <div className="glass-card tracing-panel overflow-hidden" data-testid="admin-direct-connections">
        <div className="border-b border-border-subtle px-6 py-4">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Canales de comunicación Neuralia369</p>
          <h4 className="mt-1 text-lg font-semibold text-text-heading">Conexiones directas del equipo</h4>
          <p className="mt-1 text-sm text-text-body">WhatsApp Business, Gmail y Telegram vinculados a la cuenta principal de Neuralia369.</p>
        </div>
        <div className="p-4">
          <MlmConnections api={adminNangoApi} />
        </div>
      </div>

      <AuthorizationLinksHub authFetch={authFetch} />

      {hub.launch_audit && (
        <div className="grid gap-4 xl:grid-cols-3" data-testid="integrations-launch-audit-grid">
          <SummaryPanel title="Qué vendemos" items={hub.launch_audit.what_we_sell} testId="integrations-launch-audit-sell-card" />
          <SummaryPanel title="Qué debe estar operativo ya" items={hub.launch_audit.must_be_operational_now} testId="integrations-launch-audit-now-card" />
          <SummaryPanel title="Gaps críticos antes del launch total" items={hub.launch_audit.critical_gaps_before_full_launch} testId="integrations-launch-audit-gap-card" />
        </div>
      )}

      {readiness?.items?.length > 0 && (
        <div className="glass-card tracing-panel p-6" data-testid="integrations-readiness-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Checklist inmediata</p>
              <h4 className="mt-2 text-xl font-semibold text-text-heading">Qué falta exactamente para activar WhatsApp, n8n, HeyGen y Google</h4>
            </div>
            <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-primary">P0 integrations</span>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-2" data-testid="integrations-readiness-grid">
            {readiness.items.map((item) => <IntegrationChecklistCard key={item.provider} item={item} />)}
          </div>
        </div>
      )}

      {hub.mcp_blueprint && (
        <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]" data-testid="integrations-mcp-blueprint-grid">
          <div className="glass-card tracing-panel p-6" data-testid="integrations-mcp-core-card">
            <div className="flex items-center gap-2 text-text-heading"><Sparkles className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Neuralia MCP Layer</h4></div>
            <p className="mt-3 text-sm text-text-body">Aynara será la capa principal que controla agentes, herramientas y servicios. Esta vista deja claro qué módulos componen ese hub conversacional.</p>
            <div className="mt-5 space-y-3">
              {hub.mcp_blueprint.core_modules.map((item) => (
                <div key={item.key} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`integrations-mcp-module-${item.key}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-text-heading">{item.name}</p>
                      <p className="mt-1 text-xs text-text-muted">{item.headline}</p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[item.status] || STATUS_STYLES.planned}`}>{STATUS_LABELS[item.status] || item.status}</span>
                  </div>
                  <p className="mt-3 text-sm text-text-body">{item.next_step}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card tracing-panel p-6" data-testid="integrations-mcp-services-card">
            <div className="flex items-center gap-2 text-text-heading"><Cable className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Servicios MCP premium listos para conectar</h4></div>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              {(hub.mcp_services_summary || []).map((group) => (
                <div key={group.group} className="rounded-[24px] border border-border-subtle bg-background/55 p-4" data-testid={`integrations-mcp-service-group-${group.group}`}>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{group.group}</p>
                  <p className="mt-2 text-lg font-semibold text-text-heading">{group.count} servicios</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {group.providers.map((providerKey) => <span key={providerKey} className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{providerKey}</span>)}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-[24px] border border-primary/15 bg-primary/5 p-4" data-testid="integrations-credit-packs-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Venta de créditos</p>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                {hub.mcp_blueprint.credit_packs.map((item) => (
                  <div key={item.id} className="rounded-[18px] border border-border-subtle bg-card/40 p-3" data-testid={`integrations-credit-pack-${item.id}`}>
                    <p className="text-sm font-semibold text-text-heading">{item.name}</p>
                    <p className="mt-1 text-xs text-text-muted">{item.credits} créditos · ${item.price}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-5 rounded-[24px] border border-border-subtle bg-background/55 p-5" data-testid="integrations-recommended-stack-card">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Stack recomendado final</p>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {Object.entries(hub.mcp_blueprint.recommended_stack || {}).map(([key, item]) => (
                  <div key={key} className="rounded-[18px] border border-border-subtle bg-card/40 p-4" data-testid={`integrations-recommended-stack-${key}`}>
                    <p className="text-sm font-semibold text-text-heading">{item.provider}</p>
                    <p className="mt-2 text-xs text-text-body">{item.why}</p>
                    <p className="mt-2 text-[10px] uppercase tracking-[0.16em] text-primary">env · {item.env}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="glass-card tracing-panel p-6" data-testid="integrations-browser-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Provider browser</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Filtra el stack y entra directo al bloqueo correcto</h4>
          </div>
          <button onClick={loadHub} className="btn-secondary" data-testid="integrations-hub-refresh-button"><RefreshCw className="h-4 w-4" /> Refrescar</button>
        </div>

        <div className="mt-5 grid gap-3 lg:grid-cols-[1.15fr_0.85fr_0.85fr_0.7fr]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input className="input-kinesthetic pl-11" placeholder="Buscar proveedor, servicio o credencial" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="integrations-search-input" />
          </div>
          <select className="input-kinesthetic" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="integrations-status-filter">
            <option value="all">Todos los estados</option>
            {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{STATUS_LABELS[status]}</option>)}
          </select>
          <select className="input-kinesthetic" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="integrations-category-filter">
            <option value="all">Todas las categorías</option>
            {categories.map((category) => <option key={category} value={category}>{category}</option>)}
          </select>
          <button type="button" onClick={() => { setSearch(''); setStatusFilter('all'); setCategoryFilter('all'); }} className="btn-secondary w-full" data-testid="integrations-clear-filters-button">
            <Filter className="h-4 w-4" /> Limpiar
          </button>
        </div>

        <div className="mt-5 flex flex-wrap gap-2" data-testid="integrations-status-chip-list">
          <span className="rounded-full border border-border-subtle px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-text-muted" data-testid="integrations-results-chip">Resultados · {filteredProviders.length}</span>
          {STATUS_OPTIONS.map((status) => (
            <button key={status} type="button" onClick={() => setStatusFilter(status)} className={`rounded-full px-3 py-2 text-[11px] uppercase tracking-[0.16em] ${statusFilter === status ? STATUS_STYLES[status] : 'border border-border-subtle text-text-muted'}`} data-testid={`integrations-status-chip-${status}`}>
              {STATUS_LABELS[status]}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-4" data-testid="integrations-featured-list">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Prioridad de cierre</p>
            <h4 className="mt-2 text-xl font-semibold text-text-heading">Los proveedores que más influyen en el Friday-closing</h4>
          </div>
          <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-2 text-[11px] uppercase tracking-[0.16em] text-primary">Top {featuredProviders.length}</span>
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          {featuredProviders.map((provider) => (
            <FeaturedProviderCard key={provider.provider_key} provider={provider} updateProvider={updateProvider} savingKey={savingKey} />
          ))}
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2" data-testid="integrations-provider-list">
        {filteredProviders.map((provider) => (
          <div key={provider.provider_key} className="glass-card p-5" data-testid={`integration-provider-${provider.provider_key}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{provider.category}</p>
                <h4 className="mt-2 text-xl font-semibold text-text-heading">{provider.name}</h4>
                <p className="mt-2 text-sm text-text-body">{provider.why}</p>
              </div>
              <span className={`rounded-full px-2 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[provider.status] || STATUS_STYLES.planned}`}>{STATUS_LABELS[provider.status] || provider.status}</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {provider.services.map((item) => <span key={item} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{item}</span>)}
            </div>
            <p className="mt-4 text-sm text-text-body" data-testid={`integration-provider-summary-${provider.provider_key}`}>{provider.next_step}</p>
          </div>
        ))}
      </div>
    </div>
  );
}